// Quick ghostly puff when Yorick raises a Ghoul.
// Rising green-tinted particles + an expanding ring that fades out fast.

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";

interface Particle {
  angle: number;
  speed: number;
  size: number;
  alpha: number;
}

const TOTAL_MS = 480;
const PARTICLE_COUNT = 12;

export class YorickGhoulSpawnVFX implements Animation {
  private elapsed = 0;
  private particles: Particle[];
  onEnd?: () => void;

  constructor(
    private center: Point,
    private hexSize: number,
  ) {
    this.particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      angle: (i / PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.5,
      speed: 28 + Math.random() * 50,
      size: 2.5 + Math.random() * 3,
      alpha: 0.7 + Math.random() * 0.3,
    }));
  }

  update(dt: number): void {
    this.elapsed += dt;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.center;
    const t = this.elapsed;
    const p = Math.min(t / TOTAL_MS, 1);

    ctx.save();

    // Expanding ring
    const ringR = this.hexSize * 0.9 * p;
    ctx.globalAlpha = (1 - p) * 0.75;
    ctx.strokeStyle = "#44ff88";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#00cc44";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(x, y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // Ground shadow
    if (p < 0.5) {
      const shadowAlpha = (0.5 - p) * 0.6;
      ctx.globalAlpha = shadowAlpha;
      ctx.fillStyle = "#003311";
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.ellipse(x, y, ringR * 0.7, ringR * 0.25, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Rising particles
    for (const part of this.particles) {
      const age = t / 1000;
      const dist = part.speed * age;
      const rise = age * 60;
      const px = x + Math.cos(part.angle) * dist;
      const py = y + Math.sin(part.angle) * dist - rise;
      const fadeOut = Math.max(0, 1 - p * 1.4);
      ctx.globalAlpha = part.alpha * fadeOut;
      ctx.fillStyle = "#66ffaa";
      ctx.shadowColor = "#00cc55";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(px, py, part.size * (1 - p * 0.5), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL_MS;
  }
}
